import React, { useEffect, useRef, useState } from "react";

import { trackAmplitudeEvent } from "../../amplitude";
import AddToCollectionLight from "../AddToCollectionLight";
import ClozeSentenceSearchModal from "../ClozeSentenceSearchModal";
import CompInputSentenceExplanation from "../CompInputSentenceExplanation";
import Loading from "../Loading";
import Modal from "../Modal";
import ModalFooterCloseBtn from "../ModalFooterCloseBtn";
import ModalProPromo from "../ModalProPromo";
import Toast from "../Toast";
import ClozeOptionsPanel from "./ClozeOptionsPanel";
import ClozeStoryResults from "./ClozeStoryResults";
import FeatureFeedbackSheet from "./FeatureFeedbackSheet";
import { splitIntoWordTokens } from "./clozeStoryHelpers";
import StoryAudioBar from "./StoryAudioBar";
import StoryCompletionOverlay from "./StoryCompletionOverlay";
import StoryFeedbackSheet from "./StoryFeedbackSheet";
import StoryInfoSheet from "./StoryInfoSheet";
import StorySentencePanel from "./StorySentencePanel";
import StorySettingsSheet from "./StorySettingsSheet";
import { ClozeStoryText, ReadingStoryText } from "./StoryText";
import StoryWordPanel from "./StoryWordPanel";
import { CLOZE_MODE, STORIES_EVENTS } from "./storiesEvents";
import { getDictionaryLinks, getNextStory, getReadingMinutes, goTo, requestJson, titleize } from "./storiesHelpers";
import { getStoryLevel, getStoryLevelDot, getStoryPath } from "./storyLibrary";
import { getShowStoryAudioPlayer, getStoryMode, saveShowStoryAudioPlayer, saveStoryMode } from "./storyStorage";
import useClozeStory from "./useClozeStory";
import useSaveStoryWord from "./useSaveStoryWord";
import { selectionAnchor } from "./useAnchoredPopover";
import useStoryAudio from "./useStoryAudio";
import { buildClozeTextAnswer, clozeTextSummaryToStorySummary, clozeTextToStory, getSavedClozeStatuses } from "./wikipediaStory";

const STORIES = "stories";
const WIKIPEDIA = "wikipedia";
const WIKIPEDIA_LICENSE_URL = "https://creativecommons.org/licenses/by-sa/4.0/";
const DOUBLE_CLICK_WAIT_MS = 250;
const SELECTION_SETTLE_MS = 400;
const NO_SAVED_STATUSES = {};

function StoryBadges({ story, readingMinutes }) {
  const level = getStoryLevel(story.cefrLevel);
  return (
    <div className="story-badges">
      {!!level && <span className="story-badge"><span className={`story-level-dot ${getStoryLevelDot(level)}`} /> {level}</span>}
      {!!story.category && <span className="story-badge">{titleize(story.category)}</span>}
      {!!readingMinutes && <span className="story-badge-muted">{readingMinutes} min</span>}
    </div>
  );
}

function StoryDifficulty({ story, targetLanguageName }) {
  return (
    <div className="story-details" data-testid="story-difficulty">
      <span>
        Average difficulty: {story.averageDifficulty}{" "}
        <span className="fa fa-question-circle" title={`The average ranking of words from this text on a frequency list for ${targetLanguageName}.`} />
      </span>
      <span>
        90th percentile difficulty: {story.difficulty}{" "}
        <span className="fa fa-question-circle" title="90% of the words in this text are at this difficulty or easier." />
      </span>
    </div>
  );
}

function NextStoryLink({ story, href }) {
  return (
    <a className="story-next" data-testid="story-next" href={href}>
      <div>
        <small className="text-muted">Next up</small>
        <div className="story-next-title">{story.title}</div>
        <StoryBadges readingMinutes={story.wordCount ? getReadingMinutes(story.wordCount) : null} story={story} />
      </div>
      <span className="fa fa-chevron-right" />
    </a>
  );
}

const getSelectedWord = (container) => {
  const selection = window.getSelection();
  const text = selection?.toString().trim();
  if (!text || !container?.contains(selection.anchorNode)) return null;
  const tokens = splitIntoWordTokens(text).filter((token) => token.word);
  return tokens.length ? tokens.map((token) => token.word).join(" ") : null;
};

function StoryContent(props) {
  const {
    baseLanguageCode, baseLanguageIso, clozeReadingPath, clozeSentencesUrl, collectionsUrl, compInputsUrl, featureFeedbackUrl, isPro, languagePairingSlug, nextReviewByLevel, quickCaptureEntriesUrl, quickClozeUrl,
    savedStatuses, source, stories, story, storiesPath, targetLanguageCode, targetLanguageIso, targetLanguageName, translationUrl, userClozeTextsUrl, onAnswerSaved, onProgressCompleted, onProgressReset, onStoryChange
  } = props;
  const isClozeText = source !== STORIES;
  const [mode, setMode] = useState(() => (isClozeText && Object.keys(savedStatuses).length ? CLOZE_MODE : getStoryMode()));
  const [showAudioPlayer, setShowAudioPlayer] = useState(getShowStoryAudioPlayer);
  const [activeSentence, setActiveSentence] = useState(null);
  const [selectedWord, setSelectedWord] = useState(null);
  const [selectedWordAnchor, setSelectedWordAnchor] = useState(null);
  const [openSheet, setOpenSheet] = useState(null);
  const [openModal, setOpenModal] = useState(null);
  const [isCelebrating, setIsCelebrating] = useState(false);
  const storyTextRef = useRef(null);
  const statusRef = useRef(story.status);
  statusRef.current = story.status;
  const isCompletedRef = useRef(story.status === "complete");
  const storyEventProperties = { language_pairing_slug: languagePairingSlug, mode, source, story_id: story.id };
  const flatSentences = story.paragraphs.flatMap((paragraph, paragraphIndex) =>
    paragraph.sentences.map((sentence, sentenceIndex) => ({ key: `${paragraphIndex}-${sentenceIndex}`, sentence }))
  );
  const sentences = flatSentences.map((item) => item.sentence);
  const clozeStory = useClozeStory(story, savedStatuses);
  const { saveWord, savedWords, savingWord, toast } = useSaveStoryWord({
    eventProperties: storyEventProperties,
    isPro,
    quickCaptureEntriesUrl,
    onProRequired: () => setOpenModal("proPromoWords")
  });

  const updateStatus = (status) => {
    if ((isClozeText && status !== "complete") || statusRef.current === "complete" || statusRef.current === status) return;
    onStoryChange({ ...story, status });
    if (isClozeText) onProgressCompleted();
    else requestJson({ data: { status }, method: "put", url: `${compInputsUrl}/${story.id}/progress` }).catch(() => {});
  };

  const celebrate = () => {
    setActiveSentence(null);
    setSelectedWord(null);
    setIsCelebrating(true);
  };

  const markCompleted = (completedFrom) => {
    if (isCompletedRef.current) return;
    isCompletedRef.current = true;
    trackAmplitudeEvent(STORIES_EVENTS.STORY_COMPLETED, { ...storyEventProperties, completed_from: completedFrom });
    // The last answer's PUT already completes a cloze text; a complete POST would race it and overwrite that answer.
    if (isClozeText && completedFrom === "cloze") onStoryChange({ ...story, status: "complete" });
    else updateStatus("complete");
  };

  const audio = useStoryAudio({
    sentences,
    languageIso: targetLanguageIso,
    languageCode: targetLanguageCode,
    onComplete: () => {
      markCompleted("audio");
      celebrate();
    }
  });
  const isAudioBarVisible = audio.isAvailable && showAudioPlayer;

  useEffect(() => {
    trackAmplitudeEvent(STORIES_EVENTS.STORY_OPENED, { ...storyEventProperties, cefr_level: story.cefrLevel });
    trackAmplitudeEvent(STORIES_EVENTS.STORY_MODE_SELECTED, { ...storyEventProperties, selected_by: "saved_choice" });
    if (!story.status) updateStatus("in_progress");
  }, []);

  useEffect(() => {
    if (!clozeStory.isCompletedThisSession) return;
    trackAmplitudeEvent(STORIES_EVENTS.CLOZE_COMPLETED, {
      ...storyEventProperties,
      correct_count: clozeStory.correctCount,
      incorrect_count: clozeStory.incorrectCount,
      score: clozeStory.score
    });
    markCompleted("cloze");
    celebrate();
  }, [clozeStory.isCompletedThisSession]);

  useEffect(() => {
    if (!activeSentence && !selectedWord && !clozeStory.activeItem) return;
    const closeOnEscape = (event) => {
      if (event.key !== "Escape" || openSheet || openModal) return;
      setActiveSentence(null);
      setSelectedWord(null);
      clozeStory.selectBlank(null);
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [activeSentence, selectedWord, clozeStory.activeItem, openSheet, openModal]);

  const toggleAudio = () => {
    trackAmplitudeEvent(audio.isPlaying ? STORIES_EVENTS.AUDIO_PAUSED : STORIES_EVENTS.AUDIO_PLAYED, storyEventProperties);
    if (audio.isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
  };

  const pauseAudioIfPlaying = () => {
    if (!audio.isPlaying) return false;
    audio.pause();
    return true;
  };

  const pendingSentenceRef = useRef(null);

  const showSentence = (sentence, key) => {
    if (getSelectedWord(storyTextRef.current)) return;
    setSelectedWord(null);
    clozeStory.selectBlank(null);
    setActiveSentence({ key, sentence });
    trackAmplitudeEvent(STORIES_EVENTS.TRANSLATION_USED, { ...storyEventProperties, kind: "sentence" });
  };

  const openSentence = (sentence, key, clickCount) => {
    clearTimeout(pendingSentenceRef.current);
    if (clickCount > 1) return;
    if (clickCount === 1) {
      pendingSentenceRef.current = setTimeout(() => showSentence(sentence, key), DOUBLE_CLICK_WAIT_MS);
      return;
    }
    showSentence(sentence, key);
  };

  useEffect(() => {
    let settleTimeout;
    const openWordAfterSelectionSettles = () => {
      clearTimeout(settleTimeout);
      settleTimeout = setTimeout(openSelectedWord, SELECTION_SETTLE_MS);
    };
    document.addEventListener("selectionchange", openWordAfterSelectionSettles);
    return () => {
      clearTimeout(settleTimeout);
      clearTimeout(pendingSentenceRef.current);
      document.removeEventListener("selectionchange", openWordAfterSelectionSettles);
    };
  });

  const openSelectedWord = () => {
    const word = getSelectedWord(storyTextRef.current);
    if (!word || word === selectedWord) return;
    clearTimeout(pendingSentenceRef.current);
    trackAmplitudeEvent(STORIES_EVENTS.WORD_TRANSLATION_SHOWN, { ...storyEventProperties, word });
    setActiveSentence(null);
    clozeStory.selectBlank(null);
    setSelectedWordAnchor(selectionAnchor(window.getSelection(), word));
    setSelectedWord(word);
  };

  const openBlank = (key) => {
    setActiveSentence(null);
    setSelectedWord(null);
    clozeStory.selectBlank(key);
  };

  const chooseClozeOption = (option) => {
    const activeItem = clozeStory.activeItem;
    if (!activeItem?.cloze) return;
    const isCorrect = option === activeItem.cloze.answer;
    const previousStatus = clozeStory.statuses[activeItem.key];
    trackAmplitudeEvent(STORIES_EVENTS.CLOZE_ANSWERED, { ...storyEventProperties, correct: isCorrect, first_try: !previousStatus });
    if (isClozeText) onAnswerSaved(buildClozeTextAnswer(activeItem.key, previousStatus, isCorrect));
    clozeStory.chooseOption(option);
  };

  const activeIndex = activeSentence ? flatSentences.findIndex((item) => item.key === activeSentence.key) : -1;
  const hasNextSentence = activeIndex >= 0 && activeIndex < flatSentences.length - 1;

  const openNextSentence = () => {
    const next = flatSentences[activeIndex + 1];
    setActiveSentence(next);
  };

  const switchMode = (nextMode) => {
    trackAmplitudeEvent(STORIES_EVENTS.STORY_MODE_SWITCHED, { ...storyEventProperties, mode: nextMode });
    trackAmplitudeEvent(STORIES_EVENTS.STORY_MODE_SELECTED, { ...storyEventProperties, mode: nextMode, selected_by: "user" });
    setActiveSentence(null);
    setSelectedWord(null);
    clozeStory.selectBlank(null);
    setMode(nextMode);
    saveStoryMode(nextMode);
  };

  const changeShowAudioPlayer = (isShown) => {
    if (!isShown) audio.pause();
    setShowAudioPlayer(isShown);
    saveShowStoryAudioPlayer(isShown);
  };

  const resetStory = () => {
    trackAmplitudeEvent(STORIES_EVENTS.STORY_RESET, storyEventProperties);
    setOpenSheet(null);
    setActiveSentence(null);
    setSelectedWord(null);
    audio.reset();
    clozeStory.restart();
    isCompletedRef.current = false;
    onStoryChange({ ...story, status: null });
    if (isClozeText) onProgressReset();
    else requestJson({ method: "delete", url: `${compInputsUrl}/${story.id}/progress` }).catch(() => {});
  };

  const deleteClozeText = async () => {
    await requestJson({ method: "delete", url: `${userClozeTextsUrl}/${story.slug}` });
    goTo(storiesPath);
  };

  const openInfo = () => {
    trackAmplitudeEvent(STORIES_EVENTS.STORY_INFO_OPENED, storyEventProperties);
    setOpenSheet("info");
  };

  const openAddSentence = () => setOpenModal(isPro ? "addSentence" : "proPromoSentences");

  const openExplanation = () => {
    trackAmplitudeEvent(STORIES_EVENTS.SENTENCE_EXPLAINED, storyEventProperties);
    setOpenModal("explain");
  };

  const closeModal = () => setOpenModal(null);
  const featureFeedbackProps = {
    extraInfo: `Story: ${story.title}, Level: ${story.cefrLevel || "N/A"}, Source: ${source}, Mode: ${mode}`,
    featureFeedbackUrl,
    featureName: "reading",
    targetLanguageName
  };
  const nextStory = getNextStory(stories, story);
  const storyFeedbackUrl = isClozeText ? `${clozeReadingPath}/${story.slug}/feedback` : `${compInputsUrl}/${story.id}/feedback`;
  const isClozeOptionsVisible = mode === CLOZE_MODE && !!clozeStory.activeItem?.cloze && !selectedWord;

  return (
    <div className={`story-reader${isAudioBarVisible ? " with-audio-bar" : ""}`}>
      <header className="story-reader-header">
        <div className="story-reader-title-row">
          <h1 className="story-title" data-testid="story-title">{story.title}</h1>
          <button aria-label="Story info" className="story-icon-button" data-testid="story-info-button" type="button" onClick={openInfo}>
            <span className="fa fa-info-circle" />
          </button>
          <button aria-label="Settings" className="story-icon-button" data-testid="story-settings-button" type="button" onClick={() => setOpenSheet("settings")}>
            <span className="fa fa-cog" />
          </button>
        </div>
        <p className="story-hint" data-testid="story-hint">Highlight a word to translate it, or click a sentence.</p>
      </header>

      {mode === CLOZE_MODE && !clozeStory.clozeCount && <p className="text-muted" data-testid="cloze-story-empty">This story has no blanks to fill in yet.</p>}

      <div ref={storyTextRef} onMouseUp={() => setTimeout(openSelectedWord)}>
        {mode === CLOZE_MODE ? (
          <ClozeStoryText
            activeKey={clozeStory.activeItem?.key}
            clozeParagraphs={clozeStory.story}
            selectedKey={activeSentence?.key}
            statuses={clozeStory.statuses}
            onBlankClick={openBlank}
            onSelect={openSentence}
          />
        ) : (
          <ReadingStoryText paragraphs={story.paragraphs} playingSentenceId={audio.playingSentenceId} selectedKey={activeSentence?.key} onSelect={openSentence} />
        )}
      </div>

      {!!(story.sourceUrl || story.sourceName) && (
        <p className="story-source" data-testid="story-source">
          Source:{" "}
          {story.sourceUrl ? <a href={story.sourceUrl} rel="noopener noreferrer" target="_blank">{story.sourceName || story.sourceUrl}</a> : story.sourceName}
          {source === WIKIPEDIA && <> · <a href={WIKIPEDIA_LICENSE_URL} rel="noopener noreferrer" target="_blank">CC BY-SA 4.0</a></>}
        </p>
      )}

      {!!story.difficulty && <StoryDifficulty story={story} targetLanguageName={targetLanguageName} />}

      {mode === CLOZE_MODE && clozeStory.isComplete && (
        <ClozeStoryResults
          correctCount={clozeStory.correctCount}
          incorrectCount={clozeStory.incorrectCount}
          score={clozeStory.score}
          onPlayAgain={isClozeText ? resetStory : clozeStory.restart}
        />
      )}

      {!!nextStory && (
        <NextStoryLink href={getStoryPath(storiesPath, { slug: nextStory.slug, source })} story={nextStory} />
      )}

      {!!activeSentence && (
        <StorySentencePanel
          anchorTestId={`story-sentence-${activeSentence.key}`}
          canListen={audio.isAvailable}
          hasNext={hasNextSentence}
          key={activeSentence.key}
          languageCode={targetLanguageCode}
          languageIso={targetLanguageIso}
          sentence={activeSentence.sentence}
          showExplain={!!story.translationLanguageId}
          onAddSentence={openAddSentence}
          onClose={() => !openModal && setActiveSentence(null)}
          onExplain={openExplanation}
          onListen={() => trackAmplitudeEvent(STORIES_EVENTS.SENTENCE_LISTENED, storyEventProperties)}
          onListenEnd={audio.play}
          onListenStart={pauseAudioIfPlaying}
          onNext={openNextSentence}
        />
      )}

      {!!selectedWord && (
        <StoryWordPanel
          anchor={selectedWordAnchor}
          baseLanguageIso={baseLanguageIso}
          dictionaryLinks={getDictionaryLinks({ baseLanguageCode, baseLanguageIso, targetLanguageCode, targetLanguageIso, targetLanguageName, word: selectedWord })}
          isSaved={savedWords.has(selectedWord)}
          isSaving={savingWord === selectedWord}
          targetLanguageIso={targetLanguageIso}
          translationUrl={translationUrl}
          word={selectedWord}
          onClose={() => {
            if (openModal) return;
            window.getSelection()?.removeAllRanges();
            setSelectedWord(null);
          }}
          onSave={() => saveWord(selectedWord)}
          onSentenceSearch={() => setOpenModal("sentenceSearch")}
        />
      )}

      {isClozeOptionsVisible && (
        <ClozeOptionsPanel
          anchorTestId={`cloze-story-blank-${clozeStory.activeItem.key}`}
          key={clozeStory.activeItem.key}
          options={clozeStory.activeItem.cloze.options}
          wrongPicks={clozeStory.wrongPicks[clozeStory.activeItem.key] ?? []}
          onChoose={chooseClozeOption}
          onClose={() => clozeStory.selectBlank(null)}
        />
      )}

      {isAudioBarVisible && (
        <StoryAudioBar audio={audio} sentenceCount={sentences.length} onOpenSettings={() => setOpenSheet("settings")} onToggle={toggleAudio} />
      )}

      {isCelebrating && (
        <StoryCompletionOverlay
          onDone={() => {
            setIsCelebrating(false);
            setOpenSheet(source === "mine" ? null : "feedback");
          }}
        />
      )}

      {openSheet === "settings" && (
        <StorySettingsSheet
          audio={audio}
          onSendFeedback={() => setOpenSheet("featureFeedback")}
          isCompleted={story.status === "complete"}
          mode={mode}
          showAudioPlayer={showAudioPlayer}
          onClose={() => setOpenSheet(null)}
          onDelete={source === "mine" && story.isOwn ? deleteClozeText : undefined}
          onMarkCompleted={() => {
            setOpenSheet(null);
            markCompleted("button");
            celebrate();
          }}
          onModeChange={switchMode}
          onReset={resetStory}
          onShowAudioPlayerChange={changeShowAudioPlayer}
        />
      )}
      {openSheet === "info" && <StoryInfoSheet onSendFeedback={() => setOpenSheet("featureFeedback")} onClose={() => setOpenSheet(null)} />}
      {openSheet === "featureFeedback" && <FeatureFeedbackSheet {...featureFeedbackProps} onClose={() => setOpenSheet(null)} />}
      {openSheet === "feedback" && (
        <StoryFeedbackSheet
          featureFeedbackUrl={featureFeedbackUrl}
          mode={mode}
          story={story}
          storyFeedbackUrl={storyFeedbackUrl}
          targetLanguageName={targetLanguageName}
          onClose={() => setOpenSheet(null)}
        />
      )}

      {!!toast && <Toast content={toast.content} context={toast.context} key={toast.id} maxWidth={300} />}

      {openModal === "addSentence" && (
        <AddToCollectionLight
          collectionsUrl={collectionsUrl}
          quickClozeUrl={quickClozeUrl}
          text={activeSentence.sentence.text}
          translation={activeSentence.sentence.translation}
          onClose={closeModal}
          onSaved={() => trackAmplitudeEvent(STORIES_EVENTS.SENTENCE_ADDED, storyEventProperties)}
        />
      )}
      {(openModal === "proPromoSentences" || openModal === "proPromoWords") && (
        <Modal footer={<ModalFooterCloseBtn />} title="Pro Feature" onHidden={closeModal}>
          <ModalProPromo can={openModal === "proPromoWords" ? "save words from stories to learn later." : "add sentences from stories to their collections."} />
        </Modal>
      )}
      {openModal === "sentenceSearch" && (
        <ClozeSentenceSearchModal collectionsUrl={collectionsUrl} isPro={isPro} nextReviewByLevel={nextReviewByLevel} query={selectedWord} url={clozeSentencesUrl} onHidden={closeModal} />
      )}
      {openModal === "explain" && (
        <Modal footer={<ModalFooterCloseBtn />} title="Explain" onHidden={closeModal}>
          <p className="text-muted"><em>{activeSentence.sentence.text}</em></p>
          <CompInputSentenceExplanation languageId={story.translationLanguageId} sentenceId={activeSentence.sentence.id} />
        </Modal>
      )}
    </div>
  );
}

const loadSmartStory = async ({ compInputsUrl, slug }) => {
  const [storyResponse, listResponse] = await Promise.all([
    requestJson({ url: `${compInputsUrl}/${slug}` }),
    requestJson({ url: compInputsUrl }).catch(() => ({ compInputs: [] }))
  ]);
  return { savedStatuses: NO_SAVED_STATUSES, stories: listResponse.compInputs, story: storyResponse.compInput };
};

const loadClozeTextStory = async ({ clozeTextsUrl, isPro, slug, source }) => {
  const [storyResponse, listResponse] = await Promise.all([
    requestJson({ url: `${clozeTextsUrl}/${slug}` }),
    requestJson({ url: clozeTextsUrl }).catch(() => ({ clozeTexts: [], myClozeTexts: [] }))
  ]);
  return {
    savedStatuses: getSavedClozeStatuses(storyResponse.clozeText.progress),
    stories: (source === "mine" ? (listResponse.myClozeTexts ?? []).filter((clozeText) => clozeText.processingStatus === "complete") : listResponse.clozeTexts)
      .filter((clozeText) => clozeText.isFree || isPro)
      .map(clozeTextSummaryToStorySummary),
    story: clozeTextToStory(storyResponse.clozeText)
  };
};

export default function StoryReader(props) {
  const { clozeTextsUrl, slug, source } = props;
  const [loaded, setLoaded] = useState(null);
  const [hasError, setHasError] = useState(false);

  const loadStory = async () => {
    setHasError(false);
    try {
      setLoaded(await (source === STORIES ? loadSmartStory(props) : loadClozeTextStory(props)));
    } catch {
      setHasError(true);
    }
  };

  useEffect(() => {
    loadStory();
  }, []);

  const changeStory = (story) => setLoaded((previous) => ({ ...previous, story }));

  const saveAnswer = async (answer) => {
    if (!answer) return;
    try {
      const response = await requestJson({ data: { ...answer, platform: "web" }, method: "patch", url: `${clozeTextsUrl}/${slug}/progress` });
      const status = response.progress.status === "not_started" ? null : response.progress.status;
      setLoaded((previous) => ({ ...previous, story: { ...previous.story, status } }));
    } catch {}
  };

  const completeProgress = () => requestJson({ method: "post", url: `${clozeTextsUrl}/${slug}/progress/complete` }).catch(() => {});
  const resetProgress = () => requestJson({ method: "delete", url: `${clozeTextsUrl}/${slug}/progress` }).catch(() => {});

  if (hasError) {
    return (
      <div className="stories-message">
        <p>Oh no! There was an error loading this story. Please try again.</p>
        <button className="btn btn-success joystix" type="button" onClick={loadStory}>Retry</button>
      </div>
    );
  }

  if (!loaded) {
    return <Loading />;
  }

  return <StoryContent {...props} {...loaded} onAnswerSaved={saveAnswer} onProgressCompleted={completeProgress} onProgressReset={resetProgress} onStoryChange={changeStory} />;
}
